// The TASKS data layer (Module 09 §5's `sales-activities` rows — P3-E PR-E2,
// D-292 ruling 1; the edits, withdraw, the due filter, My day and the deal
// subject since P3-E2 PR E2-1, D-313): the `/sales/tasks` list, the open-task
// card on the order, the quote, the customer and the deal, My day on the
// office home, and the four writes (create, complete, change, withdraw).
// Same posture as the other sales query files: the generated api-client only
// (doctrine 3), a fresh Idempotency-Key on every mutation, `{signal}` threaded
// through every read, and every mutation invalidates exactly the reads it can
// have moved.
//
// WHAT THE CLIENT CANNOT KNOW, AND HOW THE SCREENS LIVE WITH IT. There is no
// `/v1/auth/me`, so the client never learns its own user id and cannot compare
// a task's `assignee_user_id` with "me". It does not need to: `scope=mine` (the
// route's default) IS "assigned to me", decided by the server. `scope=all`
// answers every task the caller may READ (an admin: all of them; a sales
// manager: their own and those on customers assigned to them). The complete,
// change and withdraw routes admit the ASSIGNEE or a literal group/facility
// admin, so every verb is offered to an admin on any open row, and to anyone
// else only on a row the server also returned under `mine`: never a button
// that answers 403. Withdraw adds the author rule (D-313 ruling 2), which the
// client judges from `created_by = assignee_user_id` on such a row.
//
// A TASK MOVES MORE THAN THE TASK READS. A customer's and a deal's next step
// is the earliest open task on it (spec 09 §3.12), served on the customer's
// and the deal's own reads, so a create, complete, change or withdraw also
// refreshes those: the 360 (`["sales", "customer", id]`), the customer book
// (`["sales", "customers"]`) and every deal read (`["sales", "opportunities"]`,
// the one prefix hooks/opportunity-queries.ts keys the board, the list, the
// forecast and each deal under).
// Only mounted reads refetch, so the cost is the screen the reader is on.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";
import type { SalesTasksSearch } from "../screens/tasks-search-params";
import { keepPageOnTurn } from "../../../../lib/cursor-pages";

type S = components["schemas"];

export type SalesActivity = S["SalesActivity"];
export type SalesActivityKind = S["SalesActivityKind"];
export type SalesActivityStatus = S["SalesActivityStatus"];
export type SalesActivityOrigin = S["SalesActivityOrigin"];
export type SalesActivityReason = NonNullable<S["SalesActivityReason"]>;
export type SalesActivityScope = S["SalesActivityScope"];
export type SalesActivityCreateRequest = S["SalesActivityCreateRequest"];
export type SalesActivityUpdateRequest = S["SalesActivityUpdateRequest"];
export type SalesActivityListResponse = S["SalesActivityListResponse"];
export type SalesActivitiesQuery = NonNullable<
  paths["/v1/sales-activities"]["get"]["parameters"]["query"]
>;

/** The three kinds, in the order the create form offers them. */
export const ACTIVITY_KINDS: readonly SalesActivityKind[] = ["call", "meeting", "todo"];

export const activityKeys = {
  /** Every activity read shares this prefix, so one write refreshes the list
   * screen, whichever document card is mounted and My day without knowing
   * their filters. */
  all: () => ["sales", "activities"] as const,
  list: (query: SalesActivitiesQuery, cursor: string | undefined) =>
    ["sales", "activities", query, cursor ?? null] as const,
};

/** The reads a task write can move beyond the task lists: the next step on
 * the customer and on the deal (spec 09 §3.12). */
export function invalidateTaskReads(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: activityKeys.all() });
  void qc.invalidateQueries({ queryKey: ["sales", "customer"] });
  void qc.invalidateQueries({ queryKey: ["sales", "customers"] });
  void qc.invalidateQueries({ queryKey: ["sales", "opportunities"] });
}

/** One page of `GET /v1/sales-activities`. */
export async function fetchActivitiesPage(
  query: SalesActivitiesQuery,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<SalesActivityListResponse> {
  const res = await client.GET("/v1/sales-activities", {
    ...(signal !== undefined ? { signal } : {}),
    params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
  });
  return unwrap(res, "Could not load the tasks");
}

/** The `/sales/tasks` list, one cursor page at a time (the order book's
 * cursor stack, D-560: the screen shows the page its cursor reads and moves
 * by Previous and Next). */
export function useActivities(
  query: SalesActivitiesQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: activityKeys.list(query, cursor),
    enabled,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(activityKeys.list(query, cursor)),
    queryFn: async ({ signal }) => fetchActivitiesPage(query, cursor, signal),
  });
}

/** The page size the task list asks for. */
export const TASK_PAGE_LIMIT = 50;

/**
 * The task list's address as the route's query (pure, unit-tested; spec 09
 * §4.12's wire mapping): `scope` absent is `mine`, `status` absent is `open`,
 * `customer` becomes `customer_id` and `assignee` becomes
 * `assignee_user_id`. `scope` is sent as `all` only when the reader may ask
 * for it: the switch is a literal admin's (§4.12), and a non-admin arriving on
 * an address or a shared view that says `all` reads their own tasks, because
 * only rows read under `mine` can carry Complete for them (the header).
 * `order` is sent only when it is not the declared default.
 */
export function tasksListQuery(
  search: Pick<SalesTasksSearch, "scope" | "status" | "due" | "customer" | "assignee" | "order">,
  isAdmin: boolean,
): SalesActivitiesQuery {
  return {
    scope: isAdmin && search.scope === "all" ? "all" : "mine",
    status: search.status ?? "open",
    limit: TASK_PAGE_LIMIT,
    ...(search.due !== undefined ? { due: search.due } : {}),
    ...(search.customer !== undefined ? { customer_id: search.customer } : {}),
    ...(search.assignee !== undefined ? { assignee_user_id: search.assignee } : {}),
    ...(search.order !== undefined && search.order !== "worklist" ? { order: search.order } : {}),
  };
}

/** A card's subject filter: an order's, a quote's, a customer's or a deal's
 * own id. The customer id rides along for the create form's fixed subject. */
export type TaskSubject =
  | { readonly kind: "order"; readonly id: string; readonly customerId: string | null }
  | { readonly kind: "quote"; readonly id: string; readonly customerId: string | null }
  | { readonly kind: "customer"; readonly id: string; readonly customerId: string | null }
  | { readonly kind: "opportunity"; readonly id: string; readonly customerId: string | null };

/** How many open tasks one subject's card asks for. A document carries a
 * handful at most — one per ended reminder sequence, one per unreachable push,
 * and whatever a manager added — so a second page is offered, never needed in
 * practice, and never silently cut off. */
export const DOCUMENT_TASK_LIMIT = 50;

/** The subject's own filter key on `GET /v1/sales-activities`. */
function subjectFilter(subject: TaskSubject): SalesActivitiesQuery {
  switch (subject.kind) {
    case "order":
      return { sales_order_id: subject.id };
    case "quote":
      return { quote_id: subject.id };
    case "customer":
      return { customer_id: subject.id };
    case "opportunity":
      return { opportunity_id: subject.id };
  }
}

/** The query the open-task card asks: the subject's OPEN tasks, under
 * `all` (every task on it the caller may read) or `mine` (the caller's own —
 * the second read a non-admin's card makes to know which rows it may act
 * on). */
export function documentTasksQuery(
  subject: TaskSubject,
  scope: SalesActivityScope,
): SalesActivitiesQuery {
  return { scope, status: "open", limit: DOCUMENT_TASK_LIMIT, ...subjectFilter(subject) };
}

export function useDocumentTasks(
  subject: TaskSubject,
  scope: SalesActivityScope,
  enabled: boolean,
) {
  const query = documentTasksQuery(subject, scope);
  return useQuery({
    queryKey: activityKeys.list(query, undefined),
    enabled: enabled && subject.id.length > 0,
    queryFn: async ({ signal }) => fetchActivitiesPage(query, undefined, signal),
  });
}

/** How many done tasks the customer 360's Tasks panel lists (spec 09 §4.2,
 * D-313 ruling 9: "the latest five done"). */
export const RECENT_DONE_LIMIT = 5;

/** The 360 panel's second read: the customer's latest done tasks, newest
 * first (the `worklist` order for a closed status is `updated_at` newest
 * first, so no order is sent). */
export function recentDoneQuery(customerId: string): SalesActivitiesQuery {
  return { scope: "all", status: "done", customer_id: customerId, limit: RECENT_DONE_LIMIT };
}

export function useRecentDoneTasks(customerId: string, enabled: boolean) {
  const query = recentDoneQuery(customerId);
  return useQuery({
    queryKey: activityKeys.list(query, undefined),
    enabled: enabled && customerId.length > 0,
    queryFn: async ({ signal }) => fetchActivitiesPage(query, undefined, signal),
  });
}

/** How many rows My day reads (spec 09 §4.16; the plan's "one read"). The
 * envelope carries no count (D-289), so the card never prints one. */
export const MY_DAY_LIMIT = 50;

/** My day's one read: the reader's own open tasks due today or earlier. */
export const MY_DAY_QUERY: SalesActivitiesQuery = {
  scope: "mine",
  status: "open",
  due: "through_today",
  limit: MY_DAY_LIMIT,
};

export function useMyDay(enabled: boolean) {
  return useQuery({
    queryKey: activityKeys.list(MY_DAY_QUERY, undefined),
    enabled,
    retry: false,
    queryFn: async ({ signal }) => fetchActivitiesPage(MY_DAY_QUERY, undefined, signal),
  });
}

/** `POST /v1/sales-activities` — a manual task. The 201 is the new row. */
export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SalesActivityCreateRequest) => {
      const res = await client.POST("/v1/sales-activities", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the task");
    },
    onSuccess: () => {
      invalidateTaskReads(qc);
    },
  });
}

/** `POST /v1/sales-activities/{id}/complete`. Invalidates on failure too: a
 * 409 ACTIVITY_NOT_OPEN means the row on the glass is stale (someone else
 * completed it, or the engine closed it), and the refreshed list is the answer
 * the refusal sentence points at. */
export function useCompleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/sales-activities/{id}/complete", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not complete the task");
    },
    onSettled: () => {
      invalidateTaskReads(qc);
    },
  });
}

/** `PATCH /v1/sales-activities/{id}` — move to another day, reassign, or
 * edit a manual task's words (spec 09 §3.12, D-313 ruling 1). The body names
 * only the fields that changed, and a body that would change nothing is never
 * sent (the dialogs close instead). Invalidates on failure too, for the same
 * stale-row reason as Complete. */
export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: SalesActivityUpdateRequest }) => {
      const res = await client.PATCH("/v1/sales-activities/{id}", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not change the task");
    },
    onSettled: () => {
      invalidateTaskReads(qc);
    },
  });
}

/** `POST /v1/sales-activities/{id}/withdraw` — a manual task leaves the open
 * list as `cancelled` with `WITHDRAWN` (D-313 ruling 2). No body. */
export function useWithdrawActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/sales-activities/{id}/withdraw", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not withdraw the task");
    },
    onSettled: () => {
      invalidateTaskReads(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// the verbs a reader is offered on a row (pure, unit-tested)
// ---------------------------------------------------------------------------

/** The reader as the gates see it: the area's code mirror and the literal
 * admin fact (`useSalesPermissions().isAdmin` reads literal role codes, which
 * is what narrowing 11 and 17 key on, D-224 ruling 7). */
export interface TaskReader {
  readonly has: (code: string) => boolean;
  readonly isAdmin: boolean;
}

/** An open row, a reader holding the task code, and the row either proven the
 * reader's own or the reader a literal admin: the assignee-or-admin rule the
 * complete and change routes share (§7 narrowing 11). */
function assigneeOrAdmin(
  reader: TaskReader,
  row: Pick<SalesActivity, "status">,
  isOwn: boolean,
): boolean {
  if (row.status !== "open") return false;
  if (!reader.has("sales.activity.write")) return false;
  return reader.isAdmin || isOwn;
}

/**
 * May this reader press Complete on this row?
 *
 * The complete route admits the task's ASSIGNEE or a group/facility admin,
 * and requires `sales.activity.write`. The client cannot compare ids (the
 * header), so `isOwn` is what the server said: the row came back under
 * `scope=mine`. A row the reader cannot prove is theirs gets no button.
 */
export function canCompleteTask(
  reader: TaskReader,
  row: Pick<SalesActivity, "status">,
  isOwn: boolean,
): boolean {
  return assigneeOrAdmin(reader, row, isOwn);
}

/** May this reader press Move to another day? The same rule as Complete
 * (§4.12: "Each open row offers Complete and Move to another day to the
 * assignee or an admin"). */
export function canMoveTask(
  reader: TaskReader,
  row: Pick<SalesActivity, "status">,
  isOwn: boolean,
): boolean {
  return assigneeOrAdmin(reader, row, isOwn);
}

/** May this reader press Edit? Move's rule, on a MANUAL task only: a system
 * task's words come from its reason and never change (the PATCH answers 400
 * `system_task_words`). */
export function canEditTaskWords(
  reader: TaskReader,
  row: Pick<SalesActivity, "status" | "origin">,
  isOwn: boolean,
): boolean {
  return row.origin === "manual" && assigneeOrAdmin(reader, row, isOwn);
}

/** May this reader press Reassign? A literal admin's, on any open task
 * (§4.12 "An admin also has Reassign"; §7's reassign row). */
export function canReassignTask(reader: TaskReader, row: Pick<SalesActivity, "status">): boolean {
  if (row.status !== "open") return false;
  if (!reader.has("sales.activity.write")) return false;
  return reader.isAdmin;
}

/**
 * May this reader press Withdraw? A MANUAL open task, to a literal admin, or
 * to the assignee who wrote it for themselves (§7 narrowing 17, D-313 ruling
 * 2). The client proves "the assignee" by the `mine` read and "who wrote it
 * for themselves" by `created_by = assignee_user_id` on that row. A system
 * task has no author, so nobody is offered Withdraw on one: its assignee
 * would meet 403 and an admin 409 `ACTIVITY_NOT_WITHDRAWABLE`.
 */
export function canWithdrawTask(
  reader: TaskReader,
  row: Pick<SalesActivity, "status" | "origin" | "created_by" | "assignee_user_id">,
  isOwn: boolean,
): boolean {
  if (row.status !== "open" || row.origin !== "manual") return false;
  if (!reader.has("sales.activity.write")) return false;
  if (reader.isAdmin) return true;
  return isOwn && row.created_by !== null && row.created_by === row.assignee_user_id;
}
